import type { Lang } from './i18n';

/**
 * Every endpoint this app depends on.
 *
 * `PATHS` is exported so `scripts/doctor.mjs` can verify each one against a
 * running API. There are only five: a resident's whole relationship with the
 * system is a lease, a rent figure, a payment history, a way to say it is paid
 * and a way to report a leak.
 */
export const PATHS = {
  requestCode: 'POST /v1/auth/tenant/request-code',
  verify: 'POST /v1/auth/tenant/verify',
  refresh: 'POST /v1/auth/refresh',
  logout: 'POST /v1/auth/logout',
  home: 'GET /v1/tenant/home',
  support: 'GET /v1/support',
  pay: 'POST /v1/tenant/pay',
  report: 'POST /v1/tenant/report',
} as const;

const BASE = import.meta.env.VITE_API_URL ?? '';

export type ErrorKind = 'network' | 'badPhone' | 'badCode' | 'throttled' | 'noTenancy' | 'generic';

/**
 * The API accepts E.164 only (`+` and 8–15 digits). Residents type numbers the
 * local way — `050 123 4567`, `00971…`, `971…` — and every one of those was
 * refused with a generic error. A number with no country code is taken as UAE.
 */
export function normalisePhone(raw: string): string {
  const p = raw.replace(/[^\d+]/g, '');
  if (p.startsWith('+')) return p;
  if (p.startsWith('00')) return '+' + p.slice(2);
  if (p.startsWith('971')) return '+' + p;
  if (p.startsWith('0')) return '+971' + p.slice(1);
  return '+971' + p;
}

export class ApiError extends Error {
  constructor(readonly kind: ErrorKind, readonly status = 0, readonly code?: string) {
    super(kind);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
export const setToken = (t: string | null): void => { token = t; };

/*
 * The access token lasts fifteen minutes; the refresh token buys another, once.
 * Without this the console signed everyone out four times an hour.
 */
let refreshToken: string | null = null;
let onRefreshed: ((fresh: { accessToken: string; refreshToken: string }) => void) | null = null;
/** This session's refresh token, and who to tell when it is spent and replaced. */
export const setRefresh = (
  r: string | null, cb: ((fresh: { accessToken: string; refreshToken: string }) => void) | null,
): void => { refreshToken = r; onRefreshed = cb; };

let inflight: Promise<boolean> | null = null;
/**
 * One refresh at a time. Requests that fail together share it: presenting a
 * spent refresh token again looks like theft, and ends the session.
 */
function refreshOnce(): Promise<boolean> {
  if (!refreshToken) return Promise.resolve(false);
  inflight ??= (async () => {
    try {
      const res = await fetch(BASE + '/v1/auth/refresh', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const fresh = (await res.json()) as { accessToken: string; refreshToken: string };
      token = fresh.accessToken;
      refreshToken = fresh.refreshToken;
      onRefreshed?.(fresh);
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function request<T>(method: string, path: string, body?: unknown, lang?: Lang, retried = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        'content-type': 'application/json',
        'x-burj-surface': 'tenant-web',
        // the resident's language: the sign-in code is texted in it, and signing in
        // stores it for the rent reminders and replies that follow
        ...(lang ? { 'accept-language': lang } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('network');
  }

  if (res.status === 401 && !retried && !path.startsWith('/v1/auth/') && (await refreshOnce())) {
    return request<T>(method, path, body, lang, true);
  }
  if (res.status === 401) {
    // on the sign-in routes a 401 is a wrong code, not the end of a session
    if (!path.startsWith('/v1/auth/')) window.dispatchEvent(new Event('burj:signed-out'));
    throw new ApiError('badCode', 401);
  }
  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = (payload as { error?: { code?: string } }).error?.code;
    // Map to something a resident can act on. The API's own wording is written
    // for operators and is only ever in English.
    const kind: ErrorKind =
      res.status === 404 ? 'noTenancy'
      // too_many_codes, code_locked: retrying straight away is refused again
      : res.status === 429 ? 'throttled'
      : code === 'code_invalid' || (res.status === 422 && path === '/v1/auth/tenant/verify') ? 'badCode'
      : res.status === 400 && path === '/v1/auth/tenant/request-code' ? 'badPhone'
      : 'generic';
    throw new ApiError(kind, res.status, code);
  }
  return payload as T;
}

export interface Lease {
  id: string; room: string; property: string; address: string;
  startDate: string; endDate: string; monthlyRent: number; deposit: number;
  rentPaused: boolean;
}

export interface Payment {
  id: string; amount: number; currency: string;
  received_on: string; method: string; kind: string;
}

export interface Home {
  lease: Lease;
  /** reported_paid: the resident told the office it is paid; not yet confirmed */
  nextRent: { id: string; due_date: string; amount: number; reported_paid?: boolean } | null;
  payments: Payment[];
  tenant?: { id: string; name: string } | null;
}

export type PayMethod = 'BankTransfer' | 'Cheque' | 'CreditCard' | 'Cash';

export const api = {
  requestCode: (phone: string, lang: Lang) =>
    request<{ sent: true; expiresInSeconds: number }>(
      'POST', '/v1/auth/tenant/request-code', { phone: normalisePhone(phone) }, lang),

  verify: (phone: string, code: string, lang: Lang) =>
    request<{ accessToken: string; refreshToken?: string; tenant: { id: string; name: string } }>(
      'POST', '/v1/auth/tenant/verify', { phone: normalisePhone(phone), code }, lang),

  /** Ends the session's refresh family on the server. Best effort. */
  logout: (refreshToken: string) =>
    request<void>('POST', '/v1/auth/logout', { refreshToken }).catch(() => undefined),

  home: () => request<Home>('GET', '/v1/tenant/home'),
  /** The support phone, as set in the admin console; null until it is set. */
  support: () => request<{ phone: string | null }>('GET', '/v1/support'),

  report: (b: { category: string; description: string; emergency: boolean }, lang: Lang) =>
    request<{ reference: string; emergency: boolean }>('POST', '/v1/tenant/report', b, lang),

  /** A notice for the office, not a payment: nothing is charged, and the office confirms it. */
  pay: (b: { method: PayMethod; reference?: string }, lang: Lang) =>
    request<{ reported: true; alreadyReported: boolean; amount: number }>('POST', '/v1/tenant/pay', b, lang),
};

/** The categories a resident can pick. Kept in step with the i18n `category.*` keys. */
export const CATEGORIES = [
  'Plumbing', 'Electrical', 'AirConditioning', 'Appliance',
  'Internet', 'Lock', 'Cleaning', 'Pest', 'Other',
] as const;
